// App-wide Messages "Needs you" overview, shared by the app-sidebar nav badge (how many threads
// need a reply, shown ahead of opening Messages) and by the Messages page, which seeds its first
// render from the cached snapshot so returning to the page paints instantly instead of blanking
// for a fresh load.
//
// Mirrors task-counts-store: a module-level cache fed by the overview endpoint that refreshes on
// login, on thread WS events and on a slow interval, plus a lighter recompute timer so a bot reply
// that crosses the threshold ages into the count between fetches. Normal (Messages) scope only —
// internal Chats has no attention sections.

import { fetchThreadsOverview } from '@/lib/api/threads';
import { getAuthToken, getAuthUser, subscribeAuthStateChange } from '@/lib/api/token-store';
import { subscribeUserThreadEvents } from '@/lib/realtime/chat-realtime-service';
import { THREAD_CHUNK_SIZE } from '@/lib/messages/messages-constants';
import {
  BOT_ACK_DEFAULT_MAX_HOURS,
  BOT_ACK_DEFAULT_THRESHOLD_MINUTES,
  getBotAckWaitingMinutes,
  getPatientReplyWaitingMinutes,
  getThreadRowId,
  PATIENT_REPLY_DEFAULT_MAX_HOURS,
  PATIENT_REPLY_DEFAULT_THRESHOLD_MINUTES,
} from '@/lib/messages/messages-helpers';

const NETWORK_REFRESH_MS = 60000;
// Re-derive the count from the cached lists between fetches so a bot reply that crosses the
// threshold (or ages past maxHours) updates the badge without waiting for the next request.
const RECOMPUTE_MS = 20000;
// The overview is the only source (no dedicated count endpoint), and every message emits a thread
// event, so coalesce bursts generously — the badge is advisory and the interval/recompute backstop
// it. Still well under a minute, so a fresh "needs you" item surfaces promptly.
const WS_DEBOUNCE_MS = 2500;
// A WS-driven refresh stands down if the snapshot is younger than this. Comfortably wider than
// WS_DEBOUNCE_MS so the Messages page's own (faster) reaction to the same event wins the race and
// this one becomes a no-op, rather than the two racing and both fetching.
const WS_REFRESH_MAX_AGE_MS = 5000;

let snapshot = null; // last fetchThreadsOverview() response (normal scope), or null
let needsCount = 0;
let started = false;
let networkTimer = null;
let recomputeTimer = null;
let wsDebounceTimer = null;
const listeners = new Set();

// "Needs you" = every waiting thread plus the patient-reply and bot-ack rows past the threshold
// (deduped against waiting). Same rule as the Messages page's needsCount badge, applied to the
// unfiltered overview lists — the nav badge is a global count, with no per-page group/number filter.
function computeNeedsCount(snap, nowMs) {
  const waiting = Array.isArray(snap?.attention?.waiting) ? snap.attention.waiting : [];
  const patientReply = Array.isArray(snap?.attention?.patientReply)
    ? snap.attention.patientReply
    : [];
  const botAck = Array.isArray(snap?.attention?.botAck) ? snap.attention.botAck : [];
  const threshold = Number.isFinite(snap?.settings?.botAck?.thresholdMinutes)
    ? snap.settings.botAck.thresholdMinutes
    : BOT_ACK_DEFAULT_THRESHOLD_MINUTES;
  const maxHours = Number.isFinite(snap?.settings?.botAck?.maxHours)
    ? snap.settings.botAck.maxHours
    : BOT_ACK_DEFAULT_MAX_HOURS;
  // Patient replies run on their own, longer window — reading bot-ack's here would make the badge
  // disagree with the list it is counting.
  const patientReplyThreshold = Number.isFinite(snap?.settings?.patientReply?.thresholdMinutes)
    ? snap.settings.patientReply.thresholdMinutes
    : PATIENT_REPLY_DEFAULT_THRESHOLD_MINUTES;
  const patientReplyMaxHours = Number.isFinite(snap?.settings?.patientReply?.maxHours)
    ? snap.settings.patientReply.maxHours
    : PATIENT_REPLY_DEFAULT_MAX_HOURS;
  const waitingIds = new Set(waiting.map((t) => getThreadRowId(t)));
  let count = waiting.length;
  for (const t of patientReply) {
    if (waitingIds.has(getThreadRowId(t))) continue;
    if (
      getPatientReplyWaitingMinutes(t, patientReplyThreshold, patientReplyMaxHours, nowMs) != null
    ) {
      count += 1;
    }
  }
  for (const t of botAck) {
    if (waitingIds.has(getThreadRowId(t))) continue;
    if (getBotAckWaitingMinutes(t, threshold, maxHours, nowMs) != null) count += 1;
  }
  return count;
}

function emit() {
  for (const listener of listeners) {
    try {
      listener(needsCount);
    } catch {
      /* ignore listener failures */
    }
  }
}

// Re-derive the count from the current snapshot; emit only when the number actually moved.
function recompute() {
  const next = snapshot ? computeNeedsCount(snapshot, Date.now()) : 0;
  if (next === needsCount) return;
  needsCount = next;
  emit();
}

// Identity the cached snapshot belongs to. On a profile switch the previous user's snapshot
// must be dropped IMMEDIATELY (badge + Messages warm-start read it), not merely replaced when
// the new user's first fetch eventually succeeds — a failed first fetch would otherwise leave
// user A's counts and thread sections rendering inside user B's session.
let snapshotOwnerUserId = '';
// When the current snapshot landed, so background refreshes can stand down if it is already
// fresh enough for their purposes (see the maxAgeMs option on refreshThreadsOverview).
let snapshotAtMs = 0;
// The single in-flight overview request and the user it was started for, so same-user callers
// share it instead of each issuing their own.
let inFlight = null;
let inFlightUserId = '';

function dropSnapshotIfOwnerChanged(currentUserId) {
  if (snapshot === null || snapshotOwnerUserId === currentUserId) return;
  snapshot = null;
  snapshotOwnerUserId = '';
  snapshotAtMs = 0;
  if (needsCount !== 0) {
    needsCount = 0;
    emit();
  }
}

/**
 * Refresh the shared overview snapshot. Concurrent callers for the SAME user share one request:
 * on a cold load the sidebar badge and the Messages page both want this within a few milliseconds,
 * and it is one of the largest responses the app makes.
 *
 * Errors are swallowed by default (the interval retries). Pass `rethrow` when the caller owns
 * user-facing error state for the load, as the Messages page does.
 */
export async function refreshThreadsOverview({ rethrow = false, maxAgeMs = 0 } = {}) {
  if (!getAuthToken()) {
    if (snapshot !== null || needsCount !== 0) {
      snapshot = null;
      snapshotOwnerUserId = '';
      snapshotAtMs = 0;
      needsCount = 0;
      emit();
    }
    return null;
  }
  // Identity guard: drop responses that started under a different user — a profile switch
  // mid-flight must not land user A's overview (threads, badge count) in user B's session.
  const requestUserId = String(getAuthUser()?.id || '').trim();
  dropSnapshotIfOwnerChanged(requestUserId);
  // Already fresh enough for this caller. Only background refreshes pass maxAgeMs; anything
  // reacting to a user action omits it and always refetches.
  if (
    maxAgeMs > 0 &&
    snapshot !== null &&
    snapshotOwnerUserId === requestUserId &&
    Date.now() - snapshotAtMs < maxAgeMs
  ) {
    return snapshot;
  }
  if (!inFlight || inFlightUserId !== requestUserId) {
    inFlightUserId = requestUserId;
    // `limit` is sent explicitly (rather than relying on the server default) because the Messages
    // page seeds its virtual window's offsets [0, limit) from these rows — the two must agree.
    inFlight = fetchThreadsOverview({
      limit: THREAD_CHUNK_SIZE,
      windowDays: 0,
      withTotal: true,
    }).finally(() => {
      if (inFlightUserId === requestUserId) {
        inFlight = null;
      }
    });
  }
  try {
    const next = await inFlight;
    if (String(getAuthUser()?.id || '').trim() !== requestUserId) {
      return snapshot;
    }
    snapshot = next;
    snapshotOwnerUserId = requestUserId;
    snapshotAtMs = Date.now();
    recompute();
  } catch (error) {
    if (rethrow) {
      throw error;
    }
    /* keep the previous (same-user) snapshot; the next tick retries */
  }
  return snapshot;
}

/**
 * Adopt an overview snapshot the Messages page just fetched, so the nav badge tracks the page's
 * data without waiting for this store's own refresh. The page refetches on every mutating action,
 * but ACKNOWLEDGE is special: the server's acknowledgeMessage handler broadcasts no WS event, so
 * this store would otherwise keep a stale count until its 60s interval — leaving the badge a few
 * higher than the page's "Needs you" lens after an ack. Pass the fetchThreadsOverview() wrapper
 * (normal scope only). recompute() emits only when the number actually moved.
 */
export function adoptThreadsOverviewSnapshot(next) {
  ensureStarted();
  if (!next || typeof next !== 'object') return;
  snapshot = next;
  snapshotOwnerUserId = String(getAuthUser()?.id || '').trim();
  // Counts as a fetch for freshness purposes: the Messages page just pulled this exact payload,
  // so a background refresh queued right behind it has nothing to add.
  snapshotAtMs = Date.now();
  recompute();
}

function scheduleWsRefresh() {
  if (wsDebounceTimer) return;
  wsDebounceTimer = setTimeout(() => {
    wsDebounceTimer = null;
    // The Messages page reacts to the SAME thread events on its own (300ms debounce) and feeds the
    // result back through adoptThreadsOverviewSnapshot, so without a freshness floor both fire and
    // the overview is fetched twice per burst, ~2s apart. Skipping when the snapshot is already
    // this recent costs nothing: the page's copy is the same payload this would have requested.
    void refreshThreadsOverview({ maxAgeMs: WS_REFRESH_MAX_AGE_MS });
  }, WS_DEBOUNCE_MS);
}

function ensureStarted() {
  if (started || typeof window === 'undefined') return;
  started = true;
  void refreshThreadsOverview();
  networkTimer = setInterval(() => void refreshThreadsOverview(), NETWORK_REFRESH_MS);
  recomputeTimer = setInterval(recompute, RECOMPUTE_MS);
  // Never cleared — the store lives for the app session, like the other module stores.
  void networkTimer;
  void recomputeTimer;
  subscribeAuthStateChange(() => void refreshThreadsOverview());
  // Any non-task thread event (new message, lock/flag change, ack, delete) can move the waiting /
  // bot-ack piles; task-only events are handled by task-counts-store.
  subscribeUserThreadEvents((detail) => {
    if (!String(detail?.action || '').startsWith('task')) scheduleWsRefresh();
  });
}

/**
 * Last overview response (null until the first successful fetch). The Messages page seeds its
 * initial thread list / attention sections from this so a remount paints from cache instead of
 * blanking. Callers must treat the rows as read-only.
 */
export function getThreadsOverviewSnapshot() {
  ensureStarted();
  // A freshly switched-to user must never warm-start from the previous user's snapshot: the
  // remounted Messages page reads this before the post-switch refresh has resolved.
  dropSnapshotIfOwnerChanged(String(getAuthUser()?.id || '').trim());
  return snapshot;
}

/** Count of threads that need a reply right now (0 until the first fetch). */
export function getThreadsNeedsCount() {
  ensureStarted();
  return needsCount;
}

export function subscribeThreadsNeedsCount(listener) {
  if (typeof listener !== 'function') return () => {};
  ensureStarted();
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}
